import { useNavigate } from "react-router-dom";
import { cx } from "./ui";
import {
  GridIcon,
  LogOutIcon,
  LinkIcon,
  ArrowLeftIcon,
  PaperclipIcon,
} from "./ui/Icons";

/* -------------------------------------------------------------- BrandBlock */

/**
 * Sidebar header. States what the product does in one line — Excel goes in,
 * Naqood comes out — rather than labelling the logo.
 *
 * The Naqood wordmark is dark navy artwork, so on this dark rail it always
 * sits on a white plate; without one it disappears.
 */
/**
 * Overlapping MMC → Naqood marks.
 *
 * `notch` must be the SOLID colour sitting behind the lockup — the tiles'
 * borders are painted in it so the second tile cuts a notch out of the first.
 * A transparent border would blend and lose the depth, so callers on a
 * different background pass their own.
 *
 * Sizes differ slightly by design: both marks are square, but the Naqood
 * block is visually heavier than the finer MMC mark, so it gets more inner
 * padding to make them read as equal weight.
 */
export function LogoLockup({ notch = "border-ink-900", size = 38, label = true }) {
  const box = { height: size, width: size };
  return (
    <div className="group flex items-center gap-3">
      <div className="flex shrink-0 items-center">
        <span
          style={box}
          className={cx(
            "grid place-items-center overflow-hidden rounded-[9px] border-2 bg-white p-[5px]",
            notch
          )}
        >
          <img
            src="/mmc_logo.png"
            alt="MMC Convert"
            className="h-full w-full object-contain"
          />
        </span>
        <span
          style={box}
          className={cx(
            "relative z-10 -ml-3 grid place-items-center overflow-hidden rounded-[9px] border-2 bg-white p-[6px]",
            "transition-[margin] duration-200 ease-out group-hover:-ml-2",
            "motion-reduce:transition-none motion-reduce:group-hover:-ml-3",
            notch
          )}
        >
          <img
            src="/naqood.png"
            alt="Naqood"
            className="h-full w-full object-contain"
          />
        </span>
      </div>

      {label && (
        <p className="min-w-0 truncate text-[13px] font-medium text-white">
          Excel <span className="text-brand-400">→</span> Naqood
        </p>
      )}
    </div>
  );
}

export function BrandBlock() {
  return (
    <div className="border-b border-white/10 px-5 py-3">
      <LogoLockup notch="border-ink-900" />
    </div>
  );
}

/* ------------------------------------------------------------------ Sidebar */

function NavItem({ icon: Icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={cx(
        "relative flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium transition",
        active
          ? "bg-gradient-to-r from-brand-500/20 to-transparent text-white ring-1 ring-inset ring-brand-500/30"
          : "text-ink-400 hover:bg-white/[0.06] hover:text-ink-100"
      )}
    >
      {active && (
        <span className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-brand-400" />
      )}
      <Icon size={16} className={active ? "text-brand-400" : "text-ink-500"} />
      <span className="truncate">{label}</span>
    </button>
  );
}

export function Sidebar({ user, active, children }) {
  const navigate = useNavigate();

  const initials = (user?.name || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <aside className="sticky top-0 flex h-screen w-[248px] shrink-0 flex-col bg-gradient-to-b from-ink-900 via-ink-900 to-ink-950">
      <BrandBlock />

      {/* Nav */}
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        <p className="px-2 pb-2 text-[10.5px] font-semibold uppercase tracking-widest text-ink-600">
          Workspace
        </p>
        <NavItem
          icon={GridIcon}
          label="All Files"
          active={active === "files"}
          onClick={() => navigate("/dashboard")}
        />
        {/* Its own destination, not a per-file action — attachments come from
            Xero, which is connected once for the whole account. */}
        <NavItem
          icon={PaperclipIcon}
          label="Attachments"
          active={active === "attachments"}
          onClick={() => navigate("/attachments")}
        />
        {children}
      </nav>

      {/* User */}
      <div className="border-t border-white/10 p-3">
        <div className="mb-1 flex items-center gap-2.5 rounded-lg px-2 py-1.5">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-[12px] font-bold text-white shadow-lg shadow-brand-500/20">
            {initials}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12.5px] font-medium leading-tight text-white">
              {user?.name || "—"}
            </p>
            <p className="truncate text-[11px] leading-tight text-ink-500">
              {user?.email || ""}
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            localStorage.clear();
            navigate("/");
          }}
          className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium text-ink-400 transition hover:bg-red-500/10 hover:text-red-300"
        >
          <LogOutIcon size={15} />
          Sign out
        </button>
      </div>
    </aside>
  );
}

/* ------------------------------------------------------------------- Topbar */

export function Topbar({ title, subtitle, backTo, onBack, orgSlug, actions }) {
  return (
    <header className="sticky top-0 z-30 border-b border-ink-200/80 bg-white/85 backdrop-blur-md">
      <div className="flex items-center gap-4 px-8 py-4">
        {(backTo || onBack) && (
          <button
            onClick={onBack}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-ink-500 ring-1 ring-inset ring-ink-200 transition hover:bg-ink-50 hover:text-ink-800"
            aria-label="Go back"
          >
            <ArrowLeftIcon size={17} />
          </button>
        )}

        <div className="min-w-0 flex-1">
          <h1 className="truncate h-page">{title}</h1>
          {(subtitle || orgSlug) &&
            (orgSlug ? (
              // Light surface, so the Naqood wordmark reads as-is.
              <div className="mt-1 flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-md bg-ink-100 px-2 py-1 ring-1 ring-inset ring-ink-200">
                  <img
                    src="/naqood-wordmark.png"
                    alt="Naqood"
                    className="h-3 w-auto object-contain"
                  />
                </span>
                <LinkIcon size={12} className="shrink-0 text-ink-400" />
                <span className="truncate font-mono text-[12.5px] text-ink-500">
                  {orgSlug}
                </span>
              </div>
            ) : (
              <div className="mt-0.5 t-muted">
                <span className="truncate">{subtitle}</span>
              </div>
            ))}
        </div>

        {actions && (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        )}
      </div>
    </header>
  );
}

/* ----------------------------------------------------------------- AppShell */

export function AppShell({ sidebar, children }) {
  return (
    <div className="flex min-h-screen bg-ink-100">
      {sidebar}
      <div className="flex min-w-0 flex-1 flex-col">{children}</div>
    </div>
  );
}
